/* Grouping and viewport filtering for the report spine (redesign Phase 3a).
 *
 * Pure functions, no React, so the arithmetic is unit-testable without
 * mounting a page. Everything here reads fields the SERVER derived; nothing
 * is re-derived from a rule this file invents.
 *
 * ── THE VIEWPORT FILTER HAS THREE CHIPS, AND "BOTH" IS NOT ONE OF THEM ────
 *
 * The brief asked for All / Desktop / Mobile / Both. There is no "Both".
 *
 * `finding.is_mobile` is written by the server from
 * report_format.is_mobile_finding, which answers a BOOLEAN: the finding's
 * dimension is in MOBILE_DIMS, or its evidence came from the mobile
 * screenshot. So Desktop and Mobile partition the set exactly, All is their
 * sum, and a "Both" chip would read 0 on every report ever produced.
 *
 * A chip that can never carry a count is a category with no source, which is
 * the same ruling that removed the 8px tap-spacing bar in Phase 2. The idea
 * came from the brief, not from the data.
 *
 * IF THAT EVER CHANGES the chip becomes derivable and should come back: it
 * needs a finding that can be flagged at both viewports at once, which today
 * nothing produces. Do not re-add it by inferring "both" from an absent
 * annotation.viewport, and do not re-add it from the one corpus finding whose
 * signals disagree (is_mobile false, annotation.viewport "mobile") - that is
 * a data conflict, logged separately, not a viewport.
 */

export const VIEWPORT_ALL = 'all';
export const VIEWPORT_DESKTOP = 'desktop';
export const VIEWPORT_MOBILE = 'mobile';

/** The chips, in order. Three. See the note above before adding a fourth. */
export const VIEWPORTS = [
  { key: VIEWPORT_ALL, label: 'All' },
  { key: VIEWPORT_DESKTOP, label: 'Desktop' },
  { key: VIEWPORT_MOBILE, label: 'Mobile' },
];

/** The server's answer, never a second rule. `is_mobile` is set by
 *  _findings_for_ui from report_format.is_mobile_finding and survives the
 *  free-tier teaser, so a locked row filters the same as an open one. */
export const isMobile = (f) => Boolean(f && f.is_mobile);

export function matchesViewport(finding, viewport) {
  if (viewport === VIEWPORT_DESKTOP) return !isMobile(finding);
  if (viewport === VIEWPORT_MOBILE) return isMobile(finding);
  return true;
}

/** Counts for the three chips. Desktop + Mobile === All, by construction. */
export function viewportCounts(findings) {
  const list = findings || [];
  const mobile = list.filter(isMobile).length;
  return {
    [VIEWPORT_ALL]: list.length,
    [VIEWPORT_DESKTOP]: list.length - mobile,
    [VIEWPORT_MOBILE]: mobile,
  };
}

/* ── THE SPINE GROUPS BY COMPONENT ALONE ───────────────────────────────────
 *
 * NOT by component+dimension, which is what the LEVERAGE HEADLINE counts.
 * The two answer different questions and it is deliberate that they differ:
 *
 *   the headline  "how few groups hold most of the findings" - the tighter
 *                 key, because the looser one overstates (Phase 2 ruling)
 *   the spine     "what part of the page am I working on" - the looser key,
 *                 because a designer opens the footer once, not once per
 *                 dimension inside the footer
 *
 * So a report can say "6 groups hold 24 of 31" over a list of 5 sections.
 * That is not a contradiction and the panel says which key it counted.
 */

/** Closed-set order, matching backend/audit/components.py COMPONENTS. */
export const COMPONENT_ORDER = [
  'navigation', 'hero', 'conversion_path', 'forms', 'content', 'footer',
  'whole_page', 'other',
];

export const COMPONENT_LABELS = {
  navigation: 'Navigation',
  hero: 'Hero',
  conversion_path: 'Conversion path',
  forms: 'Forms',
  content: 'Content',
  footer: 'Footer',
  whole_page: 'Whole page',
  other: 'Elsewhere on the page',
};

export const componentLabel = (key) =>
  COMPONENT_LABELS[key] || String(key || '').replace(/_/g, ' ');

/* ── PRIMARY GROUP: FIRST IN COMPONENT_ORDER, AND NOTHING ELSE ────────────
 *
 * `components` is a LIST, deliberately - a footer CTA is genuinely both
 * `footer` and `conversion_path`, and the Phase 2 ruling kept the set union
 * because a designer opening the footer must see it there. Rendering every
 * member in full is what that ruling cost: on a real free report one finding
 * in three components rendered three times, evidence and all.
 *
 * So a finding renders ONCE, in its PRIMARY group, and appears in the others
 * as a reference row. Primary is the first entry of COMPONENT_ORDER the
 * finding carries. That is the whole rule.
 *
 * IT IS A LOOKUP, NOT A TIEBREAK. There is no severity comparison, no
 * "most specific component wins", no fallback branch - because every one of
 * those is a rule whose output depends on the finding, and the first
 * question anyone asks of a report is "why is this one HERE". A reader can
 * answer that from COMPONENT_ORDER alone, without reading this file.
 *
 * COMPONENT_ORDER is a closed set mirroring backend/audit/components.py, so
 * a component outside it cannot be primary by accident: indexOf returns -1
 * and the scan below never selects it unless nothing else matched, which
 * only happens when EVERY component is off-list. That case returns the
 * finding's own first component rather than null, so it still renders
 * somewhere. A finding that renders nowhere is the one outcome worse than a
 * finding that renders twice.
 */
export function primaryComponent(finding) {
  const comps = (finding && finding.components) || [];
  if (!comps.length) return null;
  for (const c of COMPONENT_ORDER) {
    if (comps.includes(c)) return c;
  }
  return comps[0];
}

/* ── THE REFERENCE ROW'S HEADLINE OBEYS THE TEASER RULE UNCONDITIONALLY ───
 *
 * The row must carry no digit and no quoted example, whether the finding it
 * points at is LOCKED or OPEN. There is deliberately no `locked` branch
 * here: the moment the rule forks, the OPEN case is the one that rots,
 * because on a free report index 0 is the only open finding and "it looked
 * right on the free report" is not a test.
 *
 * This mirrors free_view._LEAKY_TEASER_RE and free_view.safe_teaser_title.
 * It is a SECOND implementation of a server rule, which this repo normally
 * refuses - the justification is that the server cannot apply it here: the
 * server sanitises a finding it is WITHHOLDING, and this sanitises a
 * reference to a finding that may be fully rendered a screen away. Same
 * rule, different subject. The mirror is pinned by tests on both sides.
 *
 * `teaser_title` is NOT carried through server._findings_for_ui today, so
 * in practice a LOCKED finding arrives with its already-safe teaser in
 * `title` and passes at the second step, and an OPEN finding's full title
 * usually carries a digit and falls to the generic. That is the safe
 * direction. Carrying teaser_title through would give paid reference rows a
 * real headline; it is a backend change and is not made here.
 */
const LEAKY = /[0-9]|['"\u2018\u2019\u201c\u201d]/;

const GENERIC_BY_DIMENSION = {
  navigation: 'A navigation issue on this page',
  visualHierarchy: 'A visual hierarchy issue on this page',
  visual_hierarchy: 'A visual hierarchy issue on this page',
  friction: 'A friction point in this flow',
  friction_points: 'A friction point in this flow',
  accessibility: 'An accessibility issue on this page',
  conversion: 'A conversion issue on this page',
  conversion_design: 'A conversion issue on this page',
  mobileUX: 'A mobile experience issue',
  mobile_ux: 'A mobile experience issue',
};
const GENERIC_FALLBACK = 'An issue found on this page';

export function referenceHeadline(finding, isEntitled = false) {
  const f = finding || {};
  /* ENTITLED: the full title, always. The finding it points at renders IN
     FULL, with its evidence and its measured value, on this same page - so
     the pointer cannot disclose anything the reader does not already have.
     Withholding here bought nothing and cost every paid pointer its identity:
     a full title carries a number, fails the guard, and lands on the
     dimension generic, so a paid report's pointers all read "An accessibility
     issue on this page".

     The fork is on ENTITLEMENT, not on `locked`, and that distinction is the
     whole point. `locked` is per FINDING, so a rule forking on it runs the
     unguarded branch for whichever findings happen to be open - which on a
     free report is index 0 and nothing else, so the guarded branch would be
     the one that rots. Entitlement is per VIEWER and per PAGE: one value for
     every row, decided by the server, the same `viewer_entitled` the paywall
     reads. One predicate, not two. */
  if (isEntitled) {
    const full = String(f.title || '').trim();
    if (full) return full;
  }
  for (const candidate of [f.teaser_title, f.title]) {
    const t = String(candidate || '').trim();
    if (t && !LEAKY.test(t)) return t;
  }
  return GENERIC_BY_DIMENSION[String(f.dimension || '')] || GENERIC_FALLBACK;
}

/**
 * Group findings by the component buckets the server derived.
 *
 * A finding in two components is a MEMBER of both groups, so the counts are
 * unchanged from Phase 3a: `members.length` is the full membership and that
 * is the number the group heading prints. The group sizes therefore still
 * sum to more than the finding count, and `total` below is the DISTINCT
 * count and remains the only number that may sit next to "of".
 *
 * What changed is what a member RENDERS as. Each member carries
 * `isPrimary`; exactly one group per finding has it true (primaryComponent
 * returns one key), and the caller renders that member in full and every
 * other member as a reference row pointing at it. `primaryKey` and
 * `primaryLabel` ride along so the caller does not re-derive the
 * destination and cannot disagree with this file about where it is.
 *
 * A finding with no components at all - every finding on a non-WEB artefact,
 * where the six page regions do not apply - lands in `ungrouped` rather than
 * being dropped or forced into a bucket. Those are always primary: there is
 * no other group for them to be referenced from.
 */
export function groupByComponent(findings) {
  const list = findings || [];
  const buckets = new Map();
  const ungrouped = [];

  list.forEach((finding, index) => {
    const comps = (finding && finding.components) || [];
    if (!comps.length) {
      ungrouped.push({ finding, index, isPrimary: true, primaryKey: null, primaryLabel: null });
      return;
    }
    const primaryKey = primaryComponent(finding);
    comps.forEach((c) => {
      if (!buckets.has(c)) buckets.set(c, []);
      buckets.get(c).push({
        finding,
        index,
        isPrimary: c === primaryKey,
        primaryKey,
        primaryLabel: componentLabel(primaryKey),
      });
    });
  });

  const order = (c) => {
    const i = COMPONENT_ORDER.indexOf(c);
    return i === -1 ? COMPONENT_ORDER.length : i;
  };

  const all = Array.from(buckets.entries())
    .map(([key, members]) => ({ key, label: componentLabel(key), members }))
    // Largest first, then closed-set order so ties are stable rather than
    // insertion-ordered (which would reshuffle as the filter changes).
    .sort((a, b) => b.members.length - a.members.length || order(a.key) - order(b.key));

  /* ── A GROUP WITH NO CARDS IS NOT RENDERED ─────────────────────────────
   *
   * Every one of its members renders somewhere else, so the section is a
   * heading, a count, and N pointers away from itself. On real data
   * "Content 4" showed four pointers, two of them identical, and nothing to
   * act on - it read as four broken findings rather than as navigation.
   *
   * THE CAUSE IS STRUCTURAL, NOT BAD LUCK. Primary is the first entry of
   * COMPONENT_ORDER a finding carries. `content` sits fifth, and every
   * finding here that carries `content` also carries `navigation` or `hero`,
   * both earlier - so `content` loses every single time. ANY component late
   * in the order that never appears alone is PERMANENTLY reference-only, and
   * `content`, `footer`, `whole_page` and `other` are all exposed to it.
   *
   * That is a consequence of primary-by-order, and primary-by-order is still
   * right: the alternative is a tiebreak that depends on the finding, which
   * is exactly the emergent rule this design refused. A reader must be able
   * to answer "why is this one HERE" from COMPONENT_ORDER alone.
   *
   * `absorbed` carries what the dropped sections knew - the component, how
   * many findings touch it, and where they went - so the membership survives
   * as a fact rather than as an empty section. The caller prints it in one
   * line; it is not a row per finding, because a row whose only content is
   * "this is elsewhere" is a row of apology.
   */
  const groups = all.filter((g) => g.members.some((m) => m.isPrimary));
  const absorbed = all
    .filter((g) => !g.members.some((m) => m.isPrimary))
    .map((g) => ({
      key: g.key,
      label: g.label,
      count: g.members.length,
      destinations: Array.from(new Set(g.members.map((m) => m.primaryLabel)))
        .filter(Boolean)
        .sort(),
    }));

  return { groups, absorbed, ungrouped, total: list.length };
}

/** "Content (4), shown under Hero and Navigation" - one line for the sections
 *  that were dropped, so their membership is stated rather than deleted.
 *  Returns '' when nothing was absorbed, which is the common case. */
export function absorbedSummary(absorbed) {
  const list = (absorbed || []).filter((a) => a && a.count > 0);
  if (!list.length) return '';
  const join = (xs) => (xs.length <= 1 ? (xs[0] || '')
    : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);
  return list
    .map((a) => `${a.label} (${a.count}), shown under ${join(a.destinations)}`)
    .join('; ');
}

/* ── THE BANNER COUNTS WHAT IS OPEN, NOT WHAT IS IN THE ARRAY ─────────────
 *
 * The banner read `lockedCount + 1` where lockedCount was
 * `findings.length - 1`, which is `findings.length` written the long way
 * round: it asserted "1 of N" without ever looking at how many findings
 * were actually unlocked. It was honest only because the free trim happens
 * to leave exactly one open finding, so the claim and the truth coincided
 * by coincidence rather than by construction.
 *
 * This counts. If the trim ever stops running, or the server promotes a
 * second finding, the banner says so instead of insisting on 1.
 */
export const openFindingCount = (findings) =>
  (findings || []).filter((f) => !isLocked(f)).length;

/**
 * The one finding a free viewer sees open.
 *
 * It is index 0 and nothing else. The server chose it - report_format
 * .order_findings_for_free_tier promotes the highest-severity DESKTOP finding
 * that carries a measured value - and free_view.trim_audit_for_free then
 * teasers every other one, so index 0 is the only entry that still HAS a
 * body to render. Picking a different one here would render three empty
 * paragraphs, and re-deciding entitlement in the browser is the CSS paywall
 * this page already removed once.
 */
export const isOpenFreeFinding = (finding, index) => index === 0;

/** Locked is the SERVER's answer, read from the field, never recomputed from
 *  isPaid. free_view._teaser_finding sets it after stripping evidence,
 *  description and fix, and after passing the title through the teaser rule
 *  that bans a digit. A client that decides for itself is a second paywall
 *  with a second set of bugs. */
export const isLocked = (finding) => Boolean(finding && finding.locked);
